import { getActiveContext } from '@/lib/active-context-store';
import { apiClient } from '@/lib/api-client';
import type { RuleTreeNode, RulesReply } from './api-types';

export type Outcome<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly status: number; readonly code: string };

type Reply<T> = { data?: T; error?: unknown; response: Response };

async function call<T>(run: () => Promise<Reply<T>>): Promise<Outcome<T>> {
  try {
    const { data, error, response } = await run();
    if (response.ok && data !== undefined) return { ok: true, value: data };
    const code =
      typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
    return { ok: false, status: response.status, code };
  } catch {
    return { ok: false, status: 0, code: '' };
  }
}

function active(): { 'X-Active-Tenant-Id': string; 'X-Active-Organization-Id': string } {
  const context = getActiveContext();
  return {
    'X-Active-Tenant-Id': context?.tenantId ?? '',
    'X-Active-Organization-Id': context?.organizationId ?? '',
  };
}

export const readRules = async (version?: string): Promise<Outcome<RulesReply>> => {
  const outcome = await call(() =>
    apiClient.GET('/v1/archive/rules', {
      params: { query: version === undefined ? {} : { version }, header: active() },
    }),
  );
  if (!outcome.ok) return outcome;
  // The generated type cannot express the tree's recursion (`children` comes out as plain
  // objects); the API sends the same node shape at every depth (contracts §1), so the tree is
  // read as `RuleTreeNode`, and only the tree.
  const drives = outcome.value.drives.map((drive) => ({
    ...drive,
    tree: drive.tree as RuleTreeNode[],
  }));
  return { ok: true, value: { ...outcome.value, drives } };
};

export const readHistory = () =>
  call(() => apiClient.GET('/v1/archive/rules/history', { params: { header: active() } }));

/** `driveToken` is the user's short-lived Google token (visibility B); sent only when held. */
export const readOverview = (path: string | undefined, driveToken: string | undefined) =>
  call(() =>
    apiClient.GET('/v1/archive/overview', {
      params: {
        query: path === undefined ? {} : { path },
        header:
          driveToken === undefined ? active() : { ...active(), 'X-Drive-Access-Token': driveToken },
      },
    }),
  );

/** The caller's permissions here, with the ids the hook holds (never the module store). */
export const readPermissions = (tenantId: string, organizationId: string) =>
  call(() =>
    apiClient.GET('/v1/access/my-permissions', {
      params: {
        query: { module: 'archivio' },
        header: { 'X-Active-Tenant-Id': tenantId, 'X-Active-Organization-Id': organizationId },
      },
    }),
  );
